"use client";

import { useRef, useState, useMemo } from "react";
import Link from "next/link";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PrevNextNav, { getWorkNav } from "@/components/PrevNextNav";

function FloatingGeometry() {
  const groupRef = useRef<THREE.Group>(null);
  const mesh1Ref = useRef<THREE.Mesh>(null);
  const mesh2Ref = useRef<THREE.Mesh>(null);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    if (groupRef.current) groupRef.current.rotation.y = t * 0.01;
    if (mesh1Ref.current) {
      mesh1Ref.current.rotation.x = t * 0.05;
      mesh1Ref.current.position.y = Math.sin(t * 0.2) * 0.3;
    }
    if (mesh2Ref.current) {
      mesh2Ref.current.rotation.y = t * 0.04;
      mesh2Ref.current.position.y = Math.sin(t * 0.25 + 1) * 0.25;
    }
  });

  return (
    <group ref={groupRef}>
      <mesh ref={mesh1Ref} position={[3, 0.5, -2]}>
        <octahedronGeometry args={[1.5, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.2} transparent />
      </mesh>
      <mesh ref={mesh2Ref} position={[-2.5, -0.5, -1]}>
        <icosahedronGeometry args={[1.2, 0]} />
        <meshBasicMaterial color="#ff6900" wireframe opacity={0.15} transparent />
      </mesh>
    </group>
  );
}

function DotGrid() {
  const pointsRef = useRef<THREE.Points>(null);
  const positions = useMemo(() => {
    const pos = [];
    const gridSize = 20;
    const spacing = 0.8;
    for (let x = -gridSize / 2; x < gridSize / 2; x++) {
      for (let y = -gridSize / 2; y < gridSize / 2; y++) {
        pos.push(x * spacing, y * spacing, -5);
      }
    }
    return new Float32Array(pos);
  }, []);

  useFrame((state) => {
    if (pointsRef.current) pointsRef.current.rotation.z = state.clock.elapsedTime * 0.005;
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" args={[positions, 3]} />
      </bufferGeometry>
      <pointsMaterial color="#1a1a1a" size={0.015} opacity={0.08} transparent />
    </points>
  );
}

function Scene() {
  return (
    <>
      <FloatingGeometry />
      <DotGrid />
    </>
  );
}

const project = {
  title: "J.R. Simplot Company",
  subtitle: "A decade long enterprise agriculture technology partnership",
  heroImage: "/images/work/JR-Simplot-Company-2.jpg",
  overview:
    "J.R. Simplot Company has partnered with Mapleton Hill to modernize mission-critical agriculture and operations platforms, supporting enterprise decision making across supply chain, agronomy, and logistics. Our work spans enterprise applications, data engineering, real-time analytics, and systems integration, enabling Simplot teams to operate with greater visibility, speed, and confidence across complex operational environments.",
  services: [
    "Agricultural Operations Platforms",
    "Real-Time Field & Weather Data",
    "Supply Chain & Logistics Systems",
    "Enterprise Data Integration",
    "Automation & Decision Support",
  ],
  technologies: [
    {
      name: "Microsoft Azure",
      description: "Enterprise cloud infrastructure powering scalable applications and services.",
      icon: "cloud",
    },
    {
      name: "ASP.NET Core & C#",
      description: "Backend services and APIs built for performance and reliability.",
      icon: "code",
    },
    {
      name: "React & TypeScript",
      description: "Modern web interfaces with responsive, component-based architecture.",
      icon: "grid",
    },
    {
      name: "MuleSoft",
      description: "Enterprise integration platform connecting disparate business systems.",
      icon: "api",
    },
    {
      name: "Azure Event Hubs",
      description: "Real-time event streaming for data synchronization across systems.",
      icon: "cloud",
    },
    {
      name: "Sitecore CMS",
      description: "Enterprise content management for marketing and brand experiences.",
      icon: "document",
    },
    {
      name: "Contentful CMS",
      description: "Headless CMS powering product catalog and content delivery.",
      icon: "document",
    },
    {
      name: "iOS & Swift UI",
      description: "Native mobile applications for field operations and offline capability.",
      icon: "mobile",
    },
    {
      name: "Terraform",
      description: "Infrastructure as code for consistent, repeatable deployments.",
      icon: "grid",
    },
    {
      name: "Salesforce",
      description: "CRM integration for sales and customer relationship management.",
      icon: "database",
    },
    {
      name: "Esri ArcGIS",
      description: "Geospatial mapping and analysis for precision agriculture workflows.",
      icon: "grid",
    },
  ],
  closingImage: "/images/work/Siimplot-2-1.jpg",
};

const advisorPlatform = {
  title: "Advisor Platform",
  description:
    "A centralized platform enabling agronomists and advisors to manage accounts, field data, and recommendations across large customer portfolios.",
  technologies: ["Azure", "C#", "React", "APIs", "Terraform", "Esri ArcGIS"],
  image: "/images/work/Siimplot.jpg",
};

const advisorMobile = {
  title: "Advisor Platform Mobile",
  description:
    "A mobile experience designed for field use, giving advisors access to customer data, recommendations, and insights wherever work happens.",
  technologies: ["iOS", "Swift UI", "Azure", "APIs"],
  image: "/images/work/Siimplot-1.jpg",
};

const weatherApp = {
  title: "Real-Time Weather Insights",
  description:
    "Real-time analytics delivering localized weather intelligence to support timely, data driven decisions in the field.",
  technologies: ["iOS", "Swift UI", "APIs", "Data Services"],
  images: [
    "/images/work/Simplot-Weather-App-1.png",
    "/images/work/Simplot-Weather-App-2.png",
  ],
};

const sitecoreCMS = {
  title: "Enterprise Sitecore CMS Development",
  description:
    "Simplot's digital ecosystem spans multiple business units and audiences. We helped strengthen the Sitecore foundation so teams can publish faster, maintain governance, and support evolving content needs without sacrificing stability.",
  technologies: ["Sitecore CMS", "C#", "JavaScript", "Coveo", "Figma"],
  images: [
    { src: "/images/work/simplot-com.jpg", label: "Simplot Corporate" },
    { src: "/images/work/simplot-grower-solutions.jpg", label: "Simplot Grower Solutions" },
    { src: "/images/work/innvictis.jpg", label: "Innvictis" },
  ],
};

const logisticsAutomation = {
  title: "Packaging Logistics Automation",
  description:
    "Automated integration between internal JDE platform and third-party logistics partner's warehouse system. Built with event-driven architecture via Microsoft Event Hubs enabling bidirectional integration of Sales Orders and Shipping Confirmations with custom MuleSoft flows.",
  technologies: ["JDE", "MuleSoft", "Azure Event Hubs", "Softeon"],
  image: "/images/work/Siimplot-6.jpg",
};

const agvanceIntegration = {
  title: "Modernizing Agvance Salesforce Integration",
  description:
    "A modernized integration between Agvance and Salesforce to improve data accuracy, reduce duplication, and give teams faster access to the information they need in the systems they use every day.",
  technologies: ["MuleSoft", "Azure Event Hubs", "Salesforce", "Agvance"],
  image: "/images/work/Siimplot-5.jpg",
};

const contentfulCMS = {
  title: "Global Content Management with Contentful",
  description:
    "To support modern content operations and omnichannel delivery, we implemented Contentful to enable structured content, reusable components, and faster publishing across digital touchpoints.",
  technologies: ["Contentful CMS", "React", "TypeScript", "PIM", "APIs"],
  image: "/images/work/Siimplot-4-1.jpg",
};

export default function SimplotPage() {
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#1a1a1a] relative overflow-hidden">
      <div className="fixed inset-0 z-0" aria-hidden="true">
        <Canvas camera={{ position: [0, 0, 5], fov: 50 }}>
          <Scene />
        </Canvas>
      </div>

      <div className="relative z-10">
        <Navigation />

        <main id="main-content">

        {/* Hero */}
        <section className="pt-28 pb-8 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="mb-4">
              <Link href="/work" className="inline-flex items-center gap-2 text-sm text-[#1a1a1a]/70 hover:text-[#ff6900] transition-colors mb-4">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to Work
              </Link>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.1] tracking-tight mb-4">
                {project.title}
              </h1>
              <p className="text-xl text-[#ff6900] font-medium">
                {project.subtitle}
              </p>
            </div>
          </div>
        </section>

        {/* Hero Image */}
        <section className="px-6 pb-16">
          <div className="max-w-6xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-2xl h-[500px]">
              <img src={project.heroImage} alt={project.title} className="w-full h-full object-cover" />
            </div>
          </div>
        </section>

        {/* At a Glance */}
        <section className="py-16 px-6 bg-white border-y border-[#1a1a1a]/5">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-10 text-center">Simplot at a Glance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#ff6900]/10 flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#ff6900]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm text-[#1a1a1a]/70 uppercase tracking-wider mb-2">Industry</p>
                <p className="text-lg font-semibold">Agriculture & Food Production</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#ff6900]/10 flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#ff6900]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <p className="text-sm text-[#1a1a1a]/70 uppercase tracking-wider mb-2">Engagement</p>
                <p className="text-lg font-semibold">10+ Year Partnership</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#ff6900]/10 flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#ff6900]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-sm text-[#1a1a1a]/70 uppercase tracking-wider mb-2">Platforms</p>
                <p className="text-lg font-semibold">Web, Mobile, CMS, Integrations</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[#ff6900]/10 flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#ff6900]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <p className="text-sm text-[#1a1a1a]/70 uppercase tracking-wider mb-2">Scale</p>
                <p className="text-lg font-semibold">Enterprise Global Operations</p>
              </div>
            </div>
          </div>
        </section>

        {/* Technology Partnership Overview */}
        <section className="py-24 px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-6">Technology Partnership</h2>
            <p className="text-2xl md:text-3xl text-[#1a1a1a]/80 leading-relaxed font-light mb-12">
              {project.overview}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {project.services.map((service) => (
                <span key={service} className="px-4 py-2 bg-[#1a1a1a]/5 rounded-full text-[#1a1a1a]/70">
                  {service}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ===================== */}
        {/* AGRICULTURAL PLATFORMS */}
        {/* ===================== */}
        <section className="py-16 px-6 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-4">Agriculture & Operations Platforms</h2>
            <p className="text-2xl md:text-3xl text-white/80 leading-relaxed font-light">
              Operational platforms designed to support large-scale agricultural workflows, real-time decision making, and complex enterprise data environments.
            </p>
          </div>
        </section>

        {/* Advisor Platform */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-semibold tracking-tight mb-6">{advisorPlatform.title}</h3>
                <p className="text-lg text-[#1a1a1a]/70 leading-relaxed mb-8">
                  {advisorPlatform.description}
                </p>
                <div>
                  <span className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider block mb-3">Technologies</span>
                  <div className="flex flex-wrap gap-2">
                    {advisorPlatform.technologies.map((tech) => (
                      <span key={tech} className="px-3 py-1.5 bg-[#1a1a1a]/5 text-[#1a1a1a]/70 text-sm rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div
                className="group rounded-2xl overflow-hidden shadow-lg cursor-pointer"
                onClick={() => setLightboxImage(advisorPlatform.image)}
              >
                <img src={advisorPlatform.image} alt={advisorPlatform.title} className="w-full h-auto group-hover:scale-105 transition-transform duration-500" />
              </div>
            </div>
          </div>
        </section>

        {/* Advisor Platform Mobile */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div
                  className="group rounded-2xl overflow-hidden shadow-lg cursor-pointer"
                  onClick={() => setLightboxImage(advisorMobile.image)}
                >
                  <img src={advisorMobile.image} alt={advisorMobile.title} className="w-full h-auto group-hover:scale-105 transition-transform duration-500" />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <h3 className="text-3xl font-semibold tracking-tight mb-6">{advisorMobile.title}</h3>
                <p className="text-lg text-[#1a1a1a]/70 leading-relaxed mb-8">
                  {advisorMobile.description}
                </p>
                <div>
                  <span className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider block mb-3">Technologies</span>
                  <div className="flex flex-wrap gap-2">
                    {advisorMobile.technologies.map((tech) => (
                      <span key={tech} className="px-3 py-1.5 bg-[#1a1a1a]/5 text-[#1a1a1a]/70 text-sm rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Weather Application */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-semibold tracking-tight mb-6">{weatherApp.title}</h3>
                <p className="text-lg text-[#1a1a1a]/70 leading-relaxed mb-8">
                  {weatherApp.description}
                </p>
                <div>
                  <span className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider block mb-3">Technologies</span>
                  <div className="flex flex-wrap gap-2">
                    {weatherApp.technologies.map((tech) => (
                      <span key={tech} className="px-3 py-1.5 bg-[#1a1a1a]/5 text-[#1a1a1a]/70 text-sm rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {weatherApp.images.map((image, index) => (
                  <div
                    key={index}
                    className="group rounded-2xl overflow-hidden shadow-lg cursor-pointer"
                    onClick={() => setLightboxImage(image)}
                  >
                    <img src={image} alt={`${weatherApp.title} ${index + 1}`} className="w-full h-auto group-hover:scale-105 transition-transform duration-500" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Platform Outcomes */}
        <section className="py-16 md:py-24 px-6 bg-white/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-12 text-center">Operational & Business Outcomes</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { title: "Built for agricultural scale", description: "Platforms designed to support large volumes of data, users, and transactions across seasonal and geographic complexity." },
                { title: "Operational reliability", description: "Systems engineered to perform in production environments where uptime and accuracy directly impact operations." },
                { title: "Data-first architecture", description: "Analytics and insights embedded directly into workflows, not layered on after the fact." },
                { title: "Designed for real-world use", description: "Interfaces built for both office and field contexts, supporting the way teams actually work." },
              ].map((outcome, index) => (
                <div key={index} className="group cursor-default">
                  <div className="text-6xl font-bold text-[#ff6900]/10 group-hover:text-[#ff6900] transition-colors duration-300 mb-4">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <h3 className="text-lg font-semibold text-[#1a1a1a] mb-2">{outcome.title}</h3>
                  <p className="text-[#1a1a1a]/70 leading-relaxed">
                    {outcome.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================================ */}
        {/* CONTENT MANAGEMENT */}
        {/* ================================ */}
        <section className="py-16 px-6 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-4">Enterprise Content and Platform Management</h2>
            <p className="text-2xl md:text-3xl text-white/80 leading-relaxed font-light">
              A centralized content and platform foundation supporting multiple business units, applications, and user experiences while maintaining governance, performance, and flexibility.
            </p>
          </div>
        </section>

        {/* Sitecore CMS */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-semibold tracking-tight mb-6">{sitecoreCMS.title}</h3>
              <p className="text-lg text-[#1a1a1a]/70 leading-relaxed max-w-3xl mx-auto mb-6">
                {sitecoreCMS.description}
              </p>
              <div className="flex flex-wrap justify-center gap-2">
                {sitecoreCMS.technologies.map((tech) => (
                  <span key={tech} className="px-3 py-1.5 bg-[#1a1a1a]/5 text-[#1a1a1a]/70 text-sm rounded-full">
                    {tech}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {sitecoreCMS.images.map((image, index) => (
                <div key={index} className="group">
                  <div
                    className="rounded-2xl overflow-hidden shadow-lg cursor-pointer aspect-[4/3] mb-3"
                    onClick={() => setLightboxImage(image.src)}
                  >
                    <img
                      src={image.src}
                      alt={image.label}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <p className="text-center font-medium text-[#1a1a1a]/70">{image.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contentful CMS */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div
                className="order-2 lg:order-1 group rounded-2xl overflow-hidden shadow-lg cursor-pointer"
                onClick={() => setLightboxImage(contentfulCMS.image)}
              >
                <img src={contentfulCMS.image} alt={contentfulCMS.title} className="w-full h-auto group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="order-1 lg:order-2">
                <h3 className="text-3xl font-semibold tracking-tight mb-6">{contentfulCMS.title}</h3>
                <p className="text-lg text-[#1a1a1a]/70 leading-relaxed mb-6">
                  {contentfulCMS.description}
                </p>
                <div>
                  <span className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider block mb-3">Technologies</span>
                  <div className="flex flex-wrap gap-2">
                    {contentfulCMS.technologies.map((tech) => (
                      <span key={tech} className="px-3 py-1.5 bg-[#1a1a1a]/5 text-[#1a1a1a]/70 text-sm rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ====================================== */}
        {/* ENTERPRISE INTEGRATIONS */}
        {/* ====================================== */}
        <section className="py-16 px-6 bg-gradient-to-br from-[#1a1a1a] to-[#2d2d2d]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-4">System Integration and Automation</h2>
            <p className="text-2xl md:text-3xl text-white/80 leading-relaxed font-light">
              Simplot's platforms connect to core operational systems where accuracy and reliability matter. We delivered integrations and automation that reduce manual work, improve visibility, and keep data flowing across teams.
            </p>
          </div>
        </section>

        {/* Logistics Automation */}
        <section className="py-16 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="text-3xl font-semibold tracking-tight mb-6">{logisticsAutomation.title}</h3>
                <p className="text-lg text-[#1a1a1a]/70 leading-relaxed mb-6">
                  {logisticsAutomation.description}
                </p>
                <div>
                  <span className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider block mb-3">Technologies</span>
                  <div className="flex flex-wrap gap-2">
                    {logisticsAutomation.technologies.map((tech) => (
                      <span key={tech} className="px-3 py-1.5 bg-[#1a1a1a]/5 text-[#1a1a1a]/70 text-sm rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div
                className="group rounded-2xl overflow-hidden shadow-lg cursor-pointer"
                onClick={() => setLightboxImage(logisticsAutomation.image)}
              >
                <img src={logisticsAutomation.image} alt={logisticsAutomation.title} className="w-full h-auto group-hover:scale-105 transition-transform duration-500" />
              </div>
            </div>
          </div>
        </section>

        {/* Agvance Integration */}
        <section className="py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div
                className="order-2 lg:order-1 group rounded-2xl overflow-hidden shadow-lg cursor-pointer"
                onClick={() => setLightboxImage(agvanceIntegration.image)}
              >
                <img src={agvanceIntegration.image} alt={agvanceIntegration.title} className="w-full h-auto group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="order-1 lg:order-2">
                <h3 className="text-3xl font-semibold tracking-tight mb-6">{agvanceIntegration.title}</h3>
                <p className="text-lg text-[#1a1a1a]/70 leading-relaxed mb-6">
                  {agvanceIntegration.description}
                </p>
                <div>
                  <span className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider block mb-3">Technologies</span>
                  <div className="flex flex-wrap gap-2">
                    {agvanceIntegration.technologies.map((tech) => (
                      <span key={tech} className="px-3 py-1.5 bg-[#1a1a1a]/5 text-[#1a1a1a]/70 text-sm rounded-full">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technologies Leveraged */}
        <section className="py-24 px-6 bg-white">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-4 text-center">Technologies Leveraged</h2>
            <p className="text-lg text-[#1a1a1a]/70 leading-relaxed max-w-3xl mx-auto mb-12 text-center">
              Simplot's digital ecosystem is powered by a modern, cloud-native architecture integrating enterprise systems, field operations, and data analytics to support global agricultural operations.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {project.technologies.map((tech, index) => {
                const iconMap: Record<string, React.ReactNode> = {
                  cloud: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                    </svg>
                  ),
                  code: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  ),
                  api: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  ),
                  grid: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
                    </svg>
                  ),
                  mobile: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  ),
                  document: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  ),
                  database: (
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
                    </svg>
                  ),
                };
                return (
                  <div key={index} className="bg-[#fafafa] rounded-2xl p-6 border border-[#1a1a1a]/5 hover:shadow-lg hover:border-[#ff6900]/20 transition-all duration-300">
                    <div className="w-12 h-12 rounded-xl bg-[#ff6900]/10 flex items-center justify-center text-[#ff6900] mb-4">
                      {iconMap[tech.icon]}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{tech.name}</h3>
                    <p className="text-[#1a1a1a]/70 leading-relaxed">{tech.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Partnership Summary */}
        <section className="py-24 px-6 bg-[#1a1a1a]">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-sm font-semibold text-[#ff6900] uppercase tracking-wider mb-6">Our Partnership</h2>
            <p className="text-2xl md:text-3xl text-white/80 leading-relaxed font-light">
              Our work with J.R. Simplot reflects a long term focus on building reliable platforms that support complex agricultural operations, connect critical systems, and create a foundation that can evolve over time.
            </p>
          </div>
        </section>

        {/* Closing Image */}
        <section className="px-6 py-16">
          <div className="max-w-6xl mx-auto">
            <div
              className="group relative rounded-2xl overflow-hidden cursor-pointer"
              onClick={() => setLightboxImage(project.closingImage)}
            >
              <img src={project.closingImage} alt="Simplot agricultural fields" className="w-full h-auto group-hover:scale-105 transition-transform duration-500" />
            </div>
          </div>
        </section>

        <PrevNextNav
          {...getWorkNav("/work/simplot")}
          basePath="/work"
          baseLabel="All Work"
        />

        {/* CTA */}
        <section className="py-24 px-6 bg-gradient-to-r from-[#ff6900] to-[#c2410c] text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight mb-6">
              Ready to start your project?
            </h2>
            <p className="text-xl text-white/70 mb-10">
              Let&apos;s discuss how we can help bring your ideas to life.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-white text-[#1a1a1a] font-medium hover:bg-white/90 transition-colors"
            >
              Get in touch
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </section>

        </main>

        <Footer />
      </div>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-6 right-6 text-white/70 hover:text-white transition-colors cursor-pointer"
            onClick={() => setLightboxImage(null)}
          >
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={lightboxImage}
            alt="Full size preview"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
